import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { alpha, type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useCallback, useMemo } from 'react';
import { useDealPrompt } from '../../hooks/useDealPrompt';
import { dealPromptContextTypes } from '../../utils/types';

export type DealPromptTileHeaderProps = {
  title: string;
  titleSuggestion?: string;
};

export const DealPromptTileHeader = ({ title, titleSuggestion }: DealPromptTileHeaderProps) => {
  const { state } = useDealPrompt();
  const { contextType } = state;

  const badgeText = useMemo(() => {
    switch (contextType) {
      case dealPromptContextTypes.AUDIENCE:
        return 'AUDIENCE';
      case dealPromptContextTypes.DOMAINS:
        return 'DOMAINS';
      case dealPromptContextTypes.APPS:
        return 'APPS';
      default:
        return '';
    }
  }, [contextType]);

  const badgeColor = useMemo(() => {
    switch (contextType) {
      case dealPromptContextTypes.AUDIENCE:
        return 'charts.magenta';
      case dealPromptContextTypes.DOMAINS:
        return 'charts.indigo';
      case dealPromptContextTypes.APPS:
        return 'charts.lightBlue';
      default:
        return '';
    }
  }, [contextType]);

  const badgeBackgroundColor = useCallback(
    (theme: Theme) => {
      switch (contextType) {
        case dealPromptContextTypes.AUDIENCE:
          return alpha(theme.palette.charts.magenta, 0.16);
        case dealPromptContextTypes.DOMAINS:
          return alpha(theme.palette.charts.indigo, 0.16);
        case dealPromptContextTypes.APPS:
          return alpha(theme.palette.charts.lightBlue, 0.16);
        default:
          return '';
      }
    },
    [contextType]
  );

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: 'center', px: 2, py: 3, justifyContent: 'space-between', height: '96px' }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'flex-start', justifyContent: 'space-between', width: '100%', height: '100%' }}
      >
        <Stack sx={{ alignItems: 'flex-start', height: '100%' }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
            <Typography variant="h1">{title}</Typography>
          </Stack>

          {titleSuggestion && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary' }}
              data-test="deal-prompt-tile-title-suggestion"
            >
              {titleSuggestion}
            </Typography>
          )}
        </Stack>
        <Box
          sx={{
            height: 22,
            borderRadius: '50px',
            backgroundColor: (theme: Theme) => badgeBackgroundColor(theme),
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            px: 1,
            py: 0,
          }}
        >
          <Typography variant="overline" sx={{ color: badgeColor }}>
            {badgeText}
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
};
